/**
 * i18n labels for the permission registry (`@alinea/types/PERMISSIONS`).
 *
 * The registry exposes stable structural keys (`category`, `displayGroupKey`,
 * `displayItemKey`). All user-facing labels for those keys live here.
 *
 * Used by:
 * - `RoleFormPage` (permission picker)
 * - Any future surface that needs to render a permission to the user.
 */

import { permissionDescriptionsEn, permissionDescriptionsPtBr } from './permission-descriptions';

export const permissionsPtBr = {
  // Module / category labels (matches PermissionDefinition.category)
  category: {
    ap: 'Contas a pagar',
    ar: 'Contas a receber',
    budget: 'Orçamento',
    dashboard: 'Painel',
    fiscal: 'Fiscal',
    fpa: 'FP&A',
    gl: 'Contabilidade',
    hcm: 'Pessoas',
    identity: 'Identidade e acesso',
    integration: 'Integrações',
    op: 'Tesouraria',
    org: 'Estrutura organizacional',
    party: 'Contrapartes',
    report: 'Relatórios',
    settings: 'Configurações',
    workflow: 'Aprovações',
  },

  // Group labels (matches PermissionDefinition.displayGroupKey)
  group: {
    gl: 'Geral',
    'gl.account': 'Contas contábeis',
    'gl.closing': 'Fechamento',
    'gl.closing.task': 'Tarefas de fechamento',
    'gl.fx-recovery': 'Recuperação histórica de câmbio',
    'gl.origin-link': 'Vínculo de origem',
    'gl.reversal': 'Estornos',
    'gl.journal': 'Lançamentos manuais',
    ap: 'Geral',
    'ap.bill': 'Contas',
    'ap.expense-accrual': 'Provisões de despesa',
    'ap.statutory-treatment': 'Tratamento de guias fiscais',
    'ap.payment': 'Pagamentos',
    'ap.document': 'Documentos',
    'ap.received-nf': 'NFs recebidas',
    'ap.reimbursement': 'Reembolsos',
    'ap.contract-review': 'Revisão de contratos',
    'ap.onfly-auto-approval': 'Aprovação automática OnFly',
    'ap.vendor-knowledge': 'Conhecimento de fornecedores',
    'fiscal.obligations': 'Obrigações fiscais',
    'ap.purchase-request': 'Solicitações de compra',
    'ap.purchase-order': 'Ordens de compra',
    'budget.line': 'Linhas de orçamento',
    budget: 'Orçamentos',
    'budget.exception': 'Exceções de orçamento',
    fpa: 'Relatórios gerenciais',
    ar: 'Geral',
    'ar.service': 'Serviços de faturamento',
    party: 'Geral',
    'party.bank-info': 'Dados bancários',
    'party.vendor-bank-account': 'Contas bancárias de fornecedores',
    hcm: 'Geral',
    'hcm.payroll': 'Folha de pagamento',
    op: 'Geral',
    'op.credit-card': 'Cartões de crédito',
    org: 'Geral',
    workflow: 'Geral',
    integration: 'Geral',
    report: 'Geral',
    'identity.user': 'Usuários',
    'identity.role': 'Papéis',
    'identity.permission': 'Permissões',
    'identity.authorization': 'Controle de acesso a recursos',
    'identity.tenant': 'Tenant',
    'identity.audit-log': 'Trilha de auditoria',
    settings: 'Geral',
    dashboard: 'Painel',
  },

  // Action labels (matches PermissionDefinition.displayItemKey / action)
  item: {
    read: 'Ver',
    create: 'Criar',
    update: 'Editar',
    delete: 'Excluir',
    manage: 'Gerenciar',
    submit: 'Submeter',
    reclassify: 'Reclassificar',
    request: 'Solicitar',
    draft: 'Rascunhar',
    post: 'Lançar',
    confirm: 'Confirmar',
    propose: 'Propor',
    send: 'Despachar',
    approve: 'Aprovar',
    reject: 'Rejeitar',
    cancel: 'Cancelar',
    edit: 'Editar',
    resolve: 'Resolver',
    execute: 'Executar',
    close: 'Fechar mês',
    reopen: 'Reabrir mês',
    readAll: 'Ver todos',
    readManaged: 'Ver sob gestão',
    readOwn: 'Ver próprios',
    legalApprove: 'Aprovar jurídico',
    riskApprove: 'Aprovar risco',
    approvalOverride: 'Sobrescrever aprovações',
    settingsManage: 'Gerenciar configurações',
    invoiceClassify: 'Classificar fatura',
    invoiceReclassify: 'Reclassificar fatura',
  },

  description: permissionDescriptionsPtBr,

  // Permission matrix editor chrome (RoleFormPage)
  matrix: {
    search: 'Filtrar permissões...',
    expandAll: 'Expandir tudo',
    collapseAll: 'Recolher tudo',
    granted: '{{count}} de {{total}} concedidas',
    permissionColumn: 'Permissão',
    grantedColumn: 'Concedido',
    selectAll: 'selecionar todas',
    noResults: 'Nenhuma permissão encontrada',
    showItemKeys: 'Mostrar chaves dos itens',
  },

  // WorkOS role display-name fallbacks keyed by stable role slug.
  role: {
    'super-admin': 'Administrador Geral',
    admin: 'Administrador',
    cfo: 'CFO / Head de Finance',
    controller: 'Controller',
    'finance-manager': 'Gerente Financeiro',
    'ap-analyst': 'Analista de Contas a Pagar',
    'ar-analyst': 'Analista de Contas a Receber',
    'treasury-analyst': 'Analista de Tesouraria',
    'hr-analyst': 'Analista de Departamento Pessoal',
    'fpa-analyst': 'Analista de FP&A',
    'outsourced-accounting': 'Contabilidade Terceirizada',
    auditor: 'Auditor',
  },

  // Page-level strings (legacy catalog keys; role form uses category/group/item)
  page: {
    title: 'Permissões',
    /**
     * Subtitle is interpolated with the number of permissions in the
     * registry. Tests (`PM-01`) match `/\d+ permissões cadastradas/` —
     * keep that wording stable so e2e doesn't lock to an i18n string
     * that's likely to be tweaked.
     */
    subtitle: '{{count}} permissões cadastradas',
    search: 'Buscar permissão...',
    noResults: 'Nenhuma permissão encontrada',
    columnSlug: 'Permissão',
    columnDescription: 'Descrição',
    columnCategory: 'Módulo',
    columnGroup: 'Grupo',
    columnAction: 'Ação',
  },
} as const;

export const permissionsEn = {
  category: {
    ap: 'Accounts Payable',
    ar: 'Accounts Receivable',
    budget: 'Budget',
    dashboard: 'Dashboard',
    fiscal: 'Fiscal',
    fpa: 'FP&A',
    gl: 'General Ledger',
    hcm: 'Human Capital',
    identity: 'Identity & Access',
    integration: 'Integrations',
    op: 'Treasury / Operations',
    org: 'Organizational Structure',
    party: 'Parties',
    report: 'Reports',
    settings: 'Settings',
    workflow: 'Approvals',
  },

  group: {
    gl: 'General',
    'gl.account': 'GL Accounts',
    'gl.closing': 'Period Closing',
    'gl.closing.task': 'Closing Tasks',
    'gl.fx-recovery': 'Historical FX Recovery',
    'gl.origin-link': 'Origin Linking',
    'gl.reversal': 'Reversals',
    'gl.journal': 'Manual journal entries',
    ap: 'General',
    'ap.bill': 'Bills',
    'ap.expense-accrual': 'Expense accruals',
    'ap.statutory-treatment': 'Statutory tax-slip treatment',
    'ap.payment': 'Payments',
    'ap.document': 'Documents',
    'ap.received-nf': 'Received NFs',
    'ap.reimbursement': 'Reimbursements',
    'ap.contract-review': 'Contract review',
    'ap.onfly-auto-approval': 'OnFly auto-approval',
    'ap.vendor-knowledge': 'Vendor knowledge',
    'fiscal.obligations': 'Fiscal obligations',
    'ap.purchase-request': 'Purchase requests',
    'ap.purchase-order': 'Purchase orders',
    'budget.line': 'Budget lines',
    budget: 'Budgets',
    'budget.exception': 'Budget exceptions',
    fpa: 'Management reports',
    ar: 'General',
    'ar.service': 'Billing services',
    party: 'General',
    'party.bank-info': 'Bank Information',
    'party.vendor-bank-account': 'Vendor Bank Accounts',
    hcm: 'General',
    'hcm.payroll': 'Payroll',
    op: 'General',
    'op.credit-card': 'Credit cards',
    org: 'General',
    workflow: 'General',
    integration: 'General',
    report: 'General',
    'identity.user': 'Users',
    'identity.role': 'Roles',
    'identity.permission': 'Permissions',
    'identity.authorization': 'Resource access control',
    'identity.tenant': 'Tenant',
    'identity.audit-log': 'Audit Log',
    settings: 'General',
    dashboard: 'Dashboard',
  },

  item: {
    read: 'View',
    create: 'Create',
    update: 'Edit',
    delete: 'Delete',
    manage: 'Manage',
    submit: 'Submit',
    reclassify: 'Reclassify',
    request: 'Request',
    draft: 'Draft',
    post: 'Post',
    confirm: 'Confirm',
    propose: 'Propose',
    send: 'Dispatch',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Cancel',
    edit: 'Edit',
    resolve: 'Resolve',
    execute: 'Execute',
    close: 'Close month',
    reopen: 'Reopen month',
    readAll: 'View all',
    readManaged: 'View managed',
    readOwn: 'View own',
    legalApprove: 'Legal approve',
    riskApprove: 'Risk approve',
    approvalOverride: 'Override approvals',
    settingsManage: 'Manage settings',
    invoiceClassify: 'Classify invoice',
    invoiceReclassify: 'Reclassify invoice',
  },

  description: permissionDescriptionsEn,

  matrix: {
    search: 'Filter permissions...',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    granted: '{{count}} of {{total}} granted',
    permissionColumn: 'Permission',
    grantedColumn: 'Granted',
    selectAll: 'select all',
    noResults: 'No permissions found',
    showItemKeys: 'Show item keys',
  },

  role: {
    'super-admin': 'Super Admin',
    admin: 'Administrator',
    cfo: 'CFO / Head of Finance',
    controller: 'Controller',
    'finance-manager': 'Finance Manager',
    'ap-analyst': 'AP Analyst',
    'ar-analyst': 'AR Analyst',
    'treasury-analyst': 'Treasury Analyst',
    'hr-analyst': 'HR Analyst',
    'fpa-analyst': 'FP&A Analyst',
    'outsourced-accounting': 'Outsourced Accounting',
    auditor: 'Auditor',
  },

  page: {
    title: 'Permissions',
    subtitle: '{{count}} permissions registered',
    search: 'Search permission...',
    noResults: 'No permissions found',
    columnSlug: 'Permission',
    columnDescription: 'Description',
    columnCategory: 'Module',
    columnGroup: 'Group',
    columnAction: 'Action',
  },
} as const;
